/**
 * Generated by orval 🍺
 * Do not edit manually.
 * TrainLoop Platform V2 API
 * Model evaluation workbench API
 * OpenAPI spec version: 0.0.0
 */
import { useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import type {
  ConversationListResponse,
  HTTPValidationError,
  ListConversationsApiTracesConversationsGetParams,
  TraceDocument,
} from "../model";

import { orvalFetch } from "../../client.ts";
import type { ErrorType } from "../../client.ts";

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === "queryKey") continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export type listConversationsApiTracesConversationsGetResponse200 = {
  data: ConversationListResponse;
  status: 200;
};

export type listConversationsApiTracesConversationsGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type listConversationsApiTracesConversationsGetResponseSuccess =
  listConversationsApiTracesConversationsGetResponse200 & {
    headers: Headers;
  };
export type listConversationsApiTracesConversationsGetResponseError =
  listConversationsApiTracesConversationsGetResponse422 & {
    headers: Headers;
  };

export type listConversationsApiTracesConversationsGetResponse =
  | listConversationsApiTracesConversationsGetResponseSuccess
  | listConversationsApiTracesConversationsGetResponseError;

export const getListConversationsApiTracesConversationsGetUrl = (
  params?: ListConversationsApiTracesConversationsGetParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/traces/conversations?${stringifiedParams}`
    : `/api/traces/conversations`;
};

/**
 * Browse the team's conversations, most recently active first.
 * Each row carries the conversation's preview, so opening the list is
 * recorded in the team's audit log (TRA-1626); the search text is not.
 *
 * One gateway D1 query (gateway PR #36) — the audit log's
 * `conversation_id` column replaced the old per-day R2 prefix rollup.
 * Only requests logged after gateway migration 0013 carry a conversation
 * id, so pre-migration traffic doesn't appear here.
 *
 * The window selects WHICH conversations appear — any whose span overlaps
 * it; `first_seen`/`last_seen`/`turn_count` describe the whole run.
 * Search, the metadata filters and the sort are the gateway's own
 * (TRA-1397), so this page and the trajectories page agree on them.
 *
 * `model` (a platform Model id, what the model page sends) and
 * `model_slug` (the gateway's canonical slug) both scope the list to one
 * model. Given together and naming different models, nothing can match
 * both, so the page is empty rather than an error.
 * @summary List Conversations
 */
export const listConversationsApiTracesConversationsGet = async (
  params?: ListConversationsApiTracesConversationsGetParams,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<listConversationsApiTracesConversationsGetResponse> => {
  return orvalFetch<listConversationsApiTracesConversationsGetResponse>(
    getListConversationsApiTracesConversationsGetUrl(params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getListConversationsApiTracesConversationsGetQueryKey = (
  params?: ListConversationsApiTracesConversationsGetParams,
) => {
  return [`/api/traces/conversations`, ...(params ? [params] : [])] as const;
};

export const getListConversationsApiTracesConversationsGetQueryOptions = <
  TData = Awaited<
    ReturnType<typeof listConversationsApiTracesConversationsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  params?: ListConversationsApiTracesConversationsGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listConversationsApiTracesConversationsGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getListConversationsApiTracesConversationsGetQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listConversationsApiTracesConversationsGet>>
  > = ({ signal }) =>
    listConversationsApiTracesConversationsGet(params, {
      signal,
      ...requestOptions,
    });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listConversationsApiTracesConversationsGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListConversationsApiTracesConversationsGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof listConversationsApiTracesConversationsGet>>
>;
export type ListConversationsApiTracesConversationsGetQueryError =
  ErrorType<HTTPValidationError>;

export function useListConversationsApiTracesConversationsGet<
  TData = Awaited<
    ReturnType<typeof listConversationsApiTracesConversationsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  params: undefined | ListConversationsApiTracesConversationsGetParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listConversationsApiTracesConversationsGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof listConversationsApiTracesConversationsGet>
          >,
          TError,
          Awaited<ReturnType<typeof listConversationsApiTracesConversationsGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListConversationsApiTracesConversationsGet<
  TData = Awaited<
    ReturnType<typeof listConversationsApiTracesConversationsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  params?: ListConversationsApiTracesConversationsGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listConversationsApiTracesConversationsGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof listConversationsApiTracesConversationsGet>
          >,
          TError,
          Awaited<ReturnType<typeof listConversationsApiTracesConversationsGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListConversationsApiTracesConversationsGet<
  TData = Awaited<
    ReturnType<typeof listConversationsApiTracesConversationsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  params?: ListConversationsApiTracesConversationsGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listConversationsApiTracesConversationsGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary List Conversations
 */

export function useListConversationsApiTracesConversationsGet<
  TData = Awaited<
    ReturnType<typeof listConversationsApiTracesConversationsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  params?: ListConversationsApiTracesConversationsGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listConversationsApiTracesConversationsGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getListConversationsApiTracesConversationsGetQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type getConversationApiTracesConversationsConversationIdGetResponse200 =
  {
    data: TraceDocument[];
    status: 200;
  };

export type getConversationApiTracesConversationsConversationIdGetResponse422 =
  {
    data: HTTPValidationError;
    status: 422;
  };

export type getConversationApiTracesConversationsConversationIdGetResponseSuccess =
  getConversationApiTracesConversationsConversationIdGetResponse200 & {
    headers: Headers;
  };
export type getConversationApiTracesConversationsConversationIdGetResponseError =
  getConversationApiTracesConversationsConversationIdGetResponse422 & {
    headers: Headers;
  };

export type getConversationApiTracesConversationsConversationIdGetResponse =
  | getConversationApiTracesConversationsConversationIdGetResponseSuccess
  | getConversationApiTracesConversationsConversationIdGetResponseError;

export const getGetConversationApiTracesConversationsConversationIdGetUrl = (
  conversationId: string,
) => {
  return `/api/traces/conversations/${conversationId}`;
};

/**
 * The COMPLETE trajectory of one conversation, oldest first.
 *
 * Keys resolve from the gateway's D1 audit index in one query (TRA-1089),
 * so a thread of any span comes back whole — no date, no window, and the
 * same assembly every agent uses (`r2_client.fetch_conversation`).
 * Opening one is recorded in the team's audit log (TRA-1626).
 * @summary Get Conversation
 */
export const getConversationApiTracesConversationsConversationIdGet = async (
  conversationId: string,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getConversationApiTracesConversationsConversationIdGetResponse> => {
  return orvalFetch<getConversationApiTracesConversationsConversationIdGetResponse>(
    getGetConversationApiTracesConversationsConversationIdGetUrl(
      conversationId,
    ),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetConversationApiTracesConversationsConversationIdGetQueryKey =
  (conversationId: string) => {
    return [`/api/traces/conversations/${conversationId}`] as const;
  };

export const getGetConversationApiTracesConversationsConversationIdGetQueryOptions =
  <
    TData = Awaited<
      ReturnType<typeof getConversationApiTracesConversationsConversationIdGet>
    >,
    TError = ErrorType<HTTPValidationError>,
  >(
    conversationId: string,
    options?: {
      query?: Partial<
        UseQueryOptions<
          Awaited<
            ReturnType<
              typeof getConversationApiTracesConversationsConversationIdGet
            >
          >,
          TError,
          TData
        >
      >;
      request?: SecondParameter<typeof orvalFetch>;
    },
  ) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
      queryOptions?.queryKey ??
      getGetConversationApiTracesConversationsConversationIdGetQueryKey(
        conversationId,
      );

    const queryFn: QueryFunction<
      Awaited<
        ReturnType<
          typeof getConversationApiTracesConversationsConversationIdGet
        >
      >
    > = ({ signal }) =>
      getConversationApiTracesConversationsConversationIdGet(conversationId, {
        signal,
        ...requestOptions,
      });

    return {
      queryKey,
      queryFn,
      enabled: conversationId !== null && conversationId !== undefined,
      ...queryOptions,
    } as UseQueryOptions<
      Awaited<
        ReturnType<
          typeof getConversationApiTracesConversationsConversationIdGet
        >
      >,
      TError,
      TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
  };

export type GetConversationApiTracesConversationsConversationIdGetQueryResult =
  NonNullable<
    Awaited<
      ReturnType<typeof getConversationApiTracesConversationsConversationIdGet>
    >
  >;
export type GetConversationApiTracesConversationsConversationIdGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetConversationApiTracesConversationsConversationIdGet<
  TData = Awaited<
    ReturnType<typeof getConversationApiTracesConversationsConversationIdGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  conversationId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getConversationApiTracesConversationsConversationIdGet
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof getConversationApiTracesConversationsConversationIdGet
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof getConversationApiTracesConversationsConversationIdGet
            >
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetConversationApiTracesConversationsConversationIdGet<
  TData = Awaited<
    ReturnType<typeof getConversationApiTracesConversationsConversationIdGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  conversationId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getConversationApiTracesConversationsConversationIdGet
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof getConversationApiTracesConversationsConversationIdGet
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof getConversationApiTracesConversationsConversationIdGet
            >
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetConversationApiTracesConversationsConversationIdGet<
  TData = Awaited<
    ReturnType<typeof getConversationApiTracesConversationsConversationIdGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  conversationId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getConversationApiTracesConversationsConversationIdGet
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Conversation
 */

export function useGetConversationApiTracesConversationsConversationIdGet<
  TData = Awaited<
    ReturnType<typeof getConversationApiTracesConversationsConversationIdGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  conversationId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getConversationApiTracesConversationsConversationIdGet
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getGetConversationApiTracesConversationsConversationIdGetQueryOptions(
      conversationId,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type getTrajectoryApiTracesTrajectoryTraceKeyGetResponse200 = {
  data: TraceDocument[];
  status: 200;
};

export type getTrajectoryApiTracesTrajectoryTraceKeyGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getTrajectoryApiTracesTrajectoryTraceKeyGetResponseSuccess =
  getTrajectoryApiTracesTrajectoryTraceKeyGetResponse200 & {
    headers: Headers;
  };
export type getTrajectoryApiTracesTrajectoryTraceKeyGetResponseError =
  getTrajectoryApiTracesTrajectoryTraceKeyGetResponse422 & {
    headers: Headers;
  };

export type getTrajectoryApiTracesTrajectoryTraceKeyGetResponse =
  | getTrajectoryApiTracesTrajectoryTraceKeyGetResponseSuccess
  | getTrajectoryApiTracesTrajectoryTraceKeyGetResponseError;

export const getGetTrajectoryApiTracesTrajectoryTraceKeyGetUrl = (
  traceKey: string,
) => {
  return `/api/traces/trajectory/${traceKey}`;
};

/**
 * The whole trajectory the given trace belongs to, oldest first.
 *
 * One request instead of a conversation listing plus one GET per turn:
 * the browser's per-origin connection cap serialized the review page's
 * per-trace fan-out into ~6-wide batches, so a 30-turn run took seconds.
 * Server-side the reads run concurrently against R2, through the same
 * canonical assembly every agent uses (`r2_client.fetch_trajectory`).
 *
 * Declared before the `/{trace_key:path}` catch-all — order matters.
 * Opening one is recorded in the team's audit log (TRA-1626).
 * @summary Get Trajectory
 */
export const getTrajectoryApiTracesTrajectoryTraceKeyGet = async (
  traceKey: string,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getTrajectoryApiTracesTrajectoryTraceKeyGetResponse> => {
  return orvalFetch<getTrajectoryApiTracesTrajectoryTraceKeyGetResponse>(
    getGetTrajectoryApiTracesTrajectoryTraceKeyGetUrl(traceKey),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetTrajectoryApiTracesTrajectoryTraceKeyGetQueryKey = (
  traceKey: string,
) => {
  return [`/api/traces/trajectory/${traceKey}`] as const;
};

export const getGetTrajectoryApiTracesTrajectoryTraceKeyGetQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getTrajectoryApiTracesTrajectoryTraceKeyGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  traceKey: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTrajectoryApiTracesTrajectoryTraceKeyGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetTrajectoryApiTracesTrajectoryTraceKeyGetQueryKey(traceKey);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTrajectoryApiTracesTrajectoryTraceKeyGet>>
  > = ({ signal }) =>
    getTrajectoryApiTracesTrajectoryTraceKeyGet(traceKey, {
      signal,
      ...requestOptions,
    });

  return {
    queryKey,
    queryFn,
    enabled: traceKey !== null && traceKey !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getTrajectoryApiTracesTrajectoryTraceKeyGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetTrajectoryApiTracesTrajectoryTraceKeyGetQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getTrajectoryApiTracesTrajectoryTraceKeyGet>>
  >;
export type GetTrajectoryApiTracesTrajectoryTraceKeyGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetTrajectoryApiTracesTrajectoryTraceKeyGet<
  TData = Awaited<
    ReturnType<typeof getTrajectoryApiTracesTrajectoryTraceKeyGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  traceKey: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTrajectoryApiTracesTrajectoryTraceKeyGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getTrajectoryApiTracesTrajectoryTraceKeyGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getTrajectoryApiTracesTrajectoryTraceKeyGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetTrajectoryApiTracesTrajectoryTraceKeyGet<
  TData = Awaited<
    ReturnType<typeof getTrajectoryApiTracesTrajectoryTraceKeyGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  traceKey: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTrajectoryApiTracesTrajectoryTraceKeyGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getTrajectoryApiTracesTrajectoryTraceKeyGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getTrajectoryApiTracesTrajectoryTraceKeyGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetTrajectoryApiTracesTrajectoryTraceKeyGet<
  TData = Awaited<
    ReturnType<typeof getTrajectoryApiTracesTrajectoryTraceKeyGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  traceKey: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTrajectoryApiTracesTrajectoryTraceKeyGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Trajectory
 */

export function useGetTrajectoryApiTracesTrajectoryTraceKeyGet<
  TData = Awaited<
    ReturnType<typeof getTrajectoryApiTracesTrajectoryTraceKeyGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  traceKey: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTrajectoryApiTracesTrajectoryTraceKeyGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getGetTrajectoryApiTracesTrajectoryTraceKeyGetQueryOptions(
      traceKey,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type getTraceApiTracesTraceKeyGetResponse200 = {
  data: TraceDocument;
  status: 200;
};

export type getTraceApiTracesTraceKeyGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getTraceApiTracesTraceKeyGetResponseSuccess =
  getTraceApiTracesTraceKeyGetResponse200 & {
    headers: Headers;
  };
export type getTraceApiTracesTraceKeyGetResponseError =
  getTraceApiTracesTraceKeyGetResponse422 & {
    headers: Headers;
  };

export type getTraceApiTracesTraceKeyGetResponse =
  | getTraceApiTracesTraceKeyGetResponseSuccess
  | getTraceApiTracesTraceKeyGetResponseError;

export const getGetTraceApiTracesTraceKeyGetUrl = (traceKey: string) => {
  return `/api/traces/${traceKey}`;
};

/**
 * Fetch a single trace by its full R2 key; the open is recorded in
 * the team's audit log (TRA-1626). `read_trace` does the reading, and
 * the public `/v1/traces` route shares it.
 * @summary Get Trace
 */
export const getTraceApiTracesTraceKeyGet = async (
  traceKey: string,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getTraceApiTracesTraceKeyGetResponse> => {
  return orvalFetch<getTraceApiTracesTraceKeyGetResponse>(
    getGetTraceApiTracesTraceKeyGetUrl(traceKey),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetTraceApiTracesTraceKeyGetQueryKey = (traceKey: string) => {
  return [`/api/traces/${traceKey}`] as const;
};

export const getGetTraceApiTracesTraceKeyGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getTraceApiTracesTraceKeyGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  traceKey: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTraceApiTracesTraceKeyGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTraceApiTracesTraceKeyGetQueryKey(traceKey);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTraceApiTracesTraceKeyGet>>
  > = ({ signal }) =>
    getTraceApiTracesTraceKeyGet(traceKey, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: traceKey !== null && traceKey !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getTraceApiTracesTraceKeyGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetTraceApiTracesTraceKeyGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTraceApiTracesTraceKeyGet>>
>;
export type GetTraceApiTracesTraceKeyGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetTraceApiTracesTraceKeyGet<
  TData = Awaited<ReturnType<typeof getTraceApiTracesTraceKeyGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  traceKey: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTraceApiTracesTraceKeyGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTraceApiTracesTraceKeyGet>>,
          TError,
          Awaited<ReturnType<typeof getTraceApiTracesTraceKeyGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetTraceApiTracesTraceKeyGet<
  TData = Awaited<ReturnType<typeof getTraceApiTracesTraceKeyGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  traceKey: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTraceApiTracesTraceKeyGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTraceApiTracesTraceKeyGet>>,
          TError,
          Awaited<ReturnType<typeof getTraceApiTracesTraceKeyGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetTraceApiTracesTraceKeyGet<
  TData = Awaited<ReturnType<typeof getTraceApiTracesTraceKeyGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  traceKey: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTraceApiTracesTraceKeyGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Trace
 */

export function useGetTraceApiTracesTraceKeyGet<
  TData = Awaited<ReturnType<typeof getTraceApiTracesTraceKeyGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  traceKey: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTraceApiTracesTraceKeyGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetTraceApiTracesTraceKeyGetQueryOptions(
    traceKey,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}
